import {
  claimPhaseActionTask,
  createDashboardTable,
  deleteDashboardTable,
  reorderDashboardTables,
  retrieveDashboardActionOptions,
  retrieveDashboardCells,
  retrieveDashboardDataItemOptions,
  retrieveDashboardPhaseOptions,
  retrieveDashboardRows,
  retrieveDashboardTable,
  retrieveDashboardTables,
  retrieveDashboardUnassignedActions,
  updateDashboardTable,
} from "@/lib/data/requests/matters/dashboard-tables";
import { queryKeys } from "@/lib/data/static/query-keys";
import type { APIResponse } from "@/lib/types";
import type {
  DashboardCellsFilters,
  DashboardRowsFilters,
  DashboardUnassignedActionsFilters,
  DashboardUnassignedActionsRead,
} from "@/lib/types/MatterDashboardTable";
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";

const dashboardScope = (firmId: string, userId: string) =>
  [firmId, userId] as const;

export const dashboardTablesQueryKey = (
  firmId: string,
  userId: string,
  tabId: string,
) =>
  [
    queryKeys.matterDashboardTables,
    ...dashboardScope(firmId, userId),
    tabId,
  ] as const;

export const dashboardTablesQueryOptions = (
  firmId: string,
  userId: string,
  tabId: string,
) =>
  queryOptions({
    queryKey: dashboardTablesQueryKey(firmId, userId, tabId),
    queryFn: () => retrieveDashboardTables(tabId),
    enabled: Boolean(firmId && userId && tabId),
    refetchOnWindowFocus: true,
  });

export const dashboardTableQueryOptions = (
  firmId: string,
  userId: string,
  tableId: string,
) =>
  queryOptions({
    queryKey: [
      queryKeys.matterDashboardTables,
      firmId,
      userId,
      "detail",
      tableId,
    ],
    queryFn: () => retrieveDashboardTable(tableId),
    enabled: Boolean(firmId && userId && tableId),
  });

export const dashboardPhaseOptionsQueryOptions = (
  firmId: string,
  userId: string,
  q = "",
  pageNumber = 1,
  pageSize = 50,
) =>
  queryOptions({
    queryKey: [
      queryKeys.matterDashboardPhaseOptions,
      firmId,
      userId,
      q,
      pageNumber,
      pageSize,
    ],
    queryFn: () =>
      retrieveDashboardPhaseOptions({
        q: q || undefined,
        pageNumber,
        pageSize,
      }),
    enabled: Boolean(firmId && userId),
  });

export const dashboardActionOptionsQueryOptions = (
  firmId: string,
  userId: string,
  phaseIds: string[],
) =>
  queryOptions({
    queryKey: [
      queryKeys.matterDashboardActionOptions,
      firmId,
      userId,
      [...phaseIds].sort(),
    ],
    queryFn: () => retrieveDashboardActionOptions(phaseIds),
    enabled: Boolean(firmId && userId && phaseIds.length),
  });

export const dashboardDataItemOptionsQueryOptions = (
  firmId: string,
  userId: string,
) =>
  queryOptions({
    queryKey: [queryKeys.matterDashboardDataItemOptions, firmId, userId],
    queryFn: retrieveDashboardDataItemOptions,
    enabled: Boolean(firmId && userId),
  });

export const dashboardRowsQueryKey = (
  firmId: string,
  userId: string,
  tableId: string,
  filters: DashboardRowsFilters,
) => [queryKeys.matterDashboardRows, firmId, userId, tableId, filters] as const;

export const dashboardRowsQueryOptions = (
  firmId: string,
  userId: string,
  tableId: string,
  filters: DashboardRowsFilters = {},
) =>
  queryOptions({
    queryKey: dashboardRowsQueryKey(firmId, userId, tableId, filters),
    queryFn: ({ signal }) => retrieveDashboardRows(tableId, filters, signal),
    enabled: Boolean(firmId && userId && tableId),
    staleTime: 5_000,
    refetchOnWindowFocus: true,
  });

export const dashboardCellsQueryOptions = (
  firmId: string,
  userId: string,
  tableId: string,
  filters: DashboardCellsFilters,
) =>
  queryOptions({
    queryKey: [
      queryKeys.matterDashboardCells,
      firmId,
      userId,
      tableId,
      filters,
    ],
    queryFn: ({ signal }) => retrieveDashboardCells(tableId, filters, signal),
    enabled: Boolean(firmId && userId && tableId && filters.matterId.length),
  });

export const dashboardUnassignedActionsQueryKey = (
  firmId: string,
  userId: string,
  tableId: string,
  filters: DashboardUnassignedActionsFilters,
) =>
  [
    queryKeys.matterDashboardUnassignedActions,
    firmId,
    userId,
    tableId,
    filters,
  ] as const;

export const dashboardUnassignedActionsQueryOptions = (
  firmId: string,
  userId: string,
  tableId: string,
  filters: DashboardUnassignedActionsFilters = {},
) =>
  queryOptions({
    queryKey: dashboardUnassignedActionsQueryKey(
      firmId,
      userId,
      tableId,
      filters,
    ),
    queryFn: ({ signal }) =>
      retrieveDashboardUnassignedActions(tableId, filters, signal),
    enabled: Boolean(firmId && userId && tableId),
    staleTime: 5_000,
    refetchOnWindowFocus: true,
  });

export function useDashboardClaimActionMutation(
  firmId: string,
  userId: string,
) {
  const queryClient = useQueryClient();
  const refreshViews = async (matterId: string) => {
    const queueQueries = queryClient.getQueryCache().findAll({
      queryKey: [queryKeys.matterDashboardUnassignedActions, firmId, userId],
      type: "active",
    });
    const rowQueries = queryClient.getQueryCache().findAll({
      queryKey: [queryKeys.matterDashboardRows, firmId, userId],
      type: "active",
    });
    await queryClient.invalidateQueries({
      queryKey: [queryKeys.matterDashboardUnassignedActions, firmId, userId],
      type: "inactive",
    });
    await queryClient.invalidateQueries({
      queryKey: [queryKeys.matterDashboardRows, firmId, userId],
      type: "inactive",
    });
    await Promise.allSettled([
      ...queueQueries.map(async (query) => {
        const tableId = query.queryKey[3];
        const filters = query.queryKey[4];
        if (typeof tableId !== "string" || !filters) return;
        try {
          const response = await retrieveDashboardUnassignedActions(tableId, {
            ...(filters as DashboardUnassignedActionsFilters),
            fresh: true,
          });
          queryClient.setQueryData(query.queryKey, response);
        } catch {
          await queryClient.invalidateQueries({ queryKey: query.queryKey });
        }
      }),
      ...rowQueries.map(async (query) => {
        const tableId = query.queryKey[3];
        const filters = query.queryKey[4];
        if (typeof tableId !== "string" || !filters) return;
        try {
          const response = await retrieveDashboardRows(tableId, {
            ...(filters as DashboardRowsFilters),
            fresh: true,
          });
          queryClient.setQueryData(query.queryKey, response);
        } catch {
          await queryClient.invalidateQueries({ queryKey: query.queryKey });
        }
      }),
    ]);
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, matterId],
      }),
      queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, "user-tasks"],
      }),
      queryClient.invalidateQueries({
        queryKey: [queryKeys.matterPhases, matterId],
      }),
    ]);
  };

  return useMutation({
    mutationFn: claimPhaseActionTask,
    onMutate: async (variables) => {
      const queryKey = [
        queryKeys.matterDashboardUnassignedActions,
        firmId,
        userId,
      ];
      await queryClient.cancelQueries({ queryKey, type: "active" });
      const previous = queryClient.getQueriesData<
        APIResponse<DashboardUnassignedActionsRead>
      >({ queryKey, type: "active" });
      previous.forEach(([key, response]) => {
        if (!response) return;
        const rows = response.data.rows.filter(
          (row) => row.phaseActionId !== variables.phaseActionId,
        );
        if (rows.length === response.data.rows.length) return;
        queryClient.setQueryData(key, {
          ...response,
          data: { ...response.data, rows },
          pagination: response.pagination && {
            ...response.pagination,
            count:
              response.pagination.count === undefined
                ? undefined
                : Math.max(0, response.pagination.count - 1),
          },
        });
      });
      return { previous };
    },
    onSuccess: async (_response, variables) => {
      await Promise.all([
        refreshViews(variables.body.matterId),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterDashboardCells, firmId, userId],
        }),
      ]);
    },
    onError: async (error, variables, context) => {
      context?.previous.forEach(([key, response]) => {
        if (response) queryClient.setQueryData(key, response);
      });
      await refreshViews(variables.body.matterId);
      if (isAxiosError(error) && error.response?.status === 403) {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [queryKeys.matterDashboardTabs, firmId, userId],
          }),
          queryClient.invalidateQueries({
            queryKey: [queryKeys.matterDashboardTables, firmId, userId],
          }),
        ]);
      }
    },
  });
}

export function useDashboardTableMutations(firmId: string, userId: string) {
  const queryClient = useQueryClient();
  const refreshConfiguration = async () => {
    const activeRows = queryClient.getQueryCache().findAll({
      queryKey: [queryKeys.matterDashboardRows, firmId, userId],
      type: "active",
    });
    const activeQueue = queryClient.getQueryCache().findAll({
      queryKey: [queryKeys.matterDashboardUnassignedActions, firmId, userId],
      type: "active",
    });
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [queryKeys.matterDashboardTabs, firmId, userId],
      }),
      queryClient.invalidateQueries({
        queryKey: [queryKeys.matterDashboardTables, firmId, userId],
      }),
      queryClient.invalidateQueries({
        queryKey: [queryKeys.matterDashboardRows, firmId, userId],
        type: "inactive",
      }),
      queryClient.invalidateQueries({
        queryKey: [queryKeys.matterDashboardCells, firmId, userId],
      }),
      queryClient.invalidateQueries({
        queryKey: [queryKeys.matterDashboardUnassignedActions, firmId, userId],
        type: "inactive",
      }),
    ]);
    // Local configuration writes must bypass the server's short-lived shared
    // row cache. Keep the viewer's query key intact so its next render uses
    // this fresh snapshot, rather than a second request with cached data.
    await Promise.allSettled([
      ...activeRows.map(async (query) => {
        const tableId = query.queryKey[3];
        const filters = query.queryKey[4];
        if (typeof tableId !== "string" || !filters) return;
        try {
          const response = await retrieveDashboardRows(tableId, {
            ...(filters as DashboardRowsFilters),
            fresh: true,
          });
          queryClient.setQueryData(query.queryKey, response);
        } catch {
          await queryClient.invalidateQueries({ queryKey: query.queryKey });
        }
      }),
      ...activeQueue.map(async (query) => {
        const tableId = query.queryKey[3];
        const filters = query.queryKey[4];
        if (typeof tableId !== "string" || !filters) return;
        try {
          const response = await retrieveDashboardUnassignedActions(tableId, {
            ...(filters as DashboardUnassignedActionsFilters),
            fresh: true,
          });
          queryClient.setQueryData(query.queryKey, response);
        } catch {
          await queryClient.invalidateQueries({ queryKey: query.queryKey });
        }
      }),
    ]);
  };

  return {
    createTable: useMutation({
      mutationFn: createDashboardTable,
      onSuccess: refreshConfiguration,
    }),
    updateTable: useMutation({
      mutationFn: updateDashboardTable,
      onSuccess: refreshConfiguration,
    }),
    deleteTable: useMutation({
      mutationFn: deleteDashboardTable,
      onSuccess: refreshConfiguration,
    }),
    reorderTables: useMutation({
      mutationFn: reorderDashboardTables,
      onSuccess: refreshConfiguration,
    }),
  };
}
